<template>
  <div class="mt-6 flex flex-col">
    <!-- Column Headers -->
    <div 
      class="task-table-header grid"
      :class="activeTaskTab === 'patientTasks' ? 'grid-cols-patient' : 'grid-cols-task'"
    >
      <div></div> <!-- Checkbox column -->
      <div class="header-text">TASK</div>
      <div v-if="activeTaskTab === 'patientTasks'" class="header-text">PATIENT NAME</div>
      <div class="header-text">DESCRIPTION</div>
      <div class="header-text">DUE DATE</div>
      <div></div> <!-- Edit column -->
      <div></div> <!-- Delete column -->
    </div>

    <!-- Task Rows -->
    <div
      v-for="task in convertedTaskData"
      :key="task.task_id"
      :data-testid="`my-calendar.task-table-detail.task-row.${task.task_id}`"
      class="task-table-line grid"
      :class="[
        activeTaskTab === 'patientTasks' ? 'grid-cols-patient' : 'grid-cols-task',
        task.is_checked === 'checked' ? 'checked' : '',
        isTaskOverdue(task) ? 'overdue' : '',
        isRowLoading(task.task_id) ? 'row-loading' : ''
      ]"
    >
      <div v-if="isRowLoading(task.task_id)" class="row-loading-overlay">
        <ArrowSmall class="row-loading-icon" />
      </div>
      <Checkbox
        :data-testid="`my-calendar.task-table-detail.complete-checkbox.${task.task_id}`"
        :status="task.is_checked" 
        @changeStatus="val => toggleCheckbox(task, val)"
      />
      <div
        :data-testid="`my-calendar.task-table-detail.title-cell.${task.task_id}`"
        class="text-truncate cell-height"
        :class="!isEditing(task, 'title') && task.is_checked === 'uncheck' ? 'inline-editable' : ''"
        @click="() => { if (!isEditing(task, 'title') && !isRowLoading(task.task_id)) startInlineEdit(task, 'title') }"
      >
        <div v-if="isEditing(task, 'title')" class="inline-editor inline-editor-title">
          <Input
            :data-testid="`my-calendar.task-table-detail.title-input.${task.task_id}`"
            class="inline-input"
            :ref="el => setEditorRef(task.task_id, 'title', el)"
            v-model="editDrafts[task.task_id].title"
            @blur="() => saveInlineEdit(task, 'title')"
            @keydown="e => handleEditorKeydown(e, task, 'title')"
          />
        </div>
        <span
          v-else
          :data-testid="`my-calendar.task-table-detail.title-text.${task.task_id}`"
          class="title-font"
        >{{ task.title }}</span>
      </div>
      <div v-if="activeTaskTab==='patientTasks'" class="flex items-center gap-1 text-truncate cell-height">
        <img v-if="task.is_checked==='checked'" src="@/assets/my-calendar/task/gray-patient.svg"/>
        <img v-else src="@/assets/my-calendar/task/blue-patient.svg"/>
        <span :data-testid="`my-calendar.task-table-detail.patient-name-text.${task.task_id}`" class="name-font">{{ formattedPatientName(task.calendar_id) }}</span>
      </div>
      <div
        :data-testid="`my-calendar.task-table-detail.description-cell.${task.task_id}`"
        class="text-truncate cell-height"
        :class="!isEditing(task, 'description') && task.is_checked === 'uncheck' ? 'inline-editable' : ''"
        @click="() => { if (!isEditing(task, 'description') && !isRowLoading(task.task_id)) startInlineEdit(task, 'description') }"
      >
        <div v-if="isEditing(task, 'description')" class="inline-editor inline-editor-description">
          <Input
            :ref="el => setEditorRef(task.task_id, 'description', el)"
            v-model="editDrafts[task.task_id].description"
            :data-testid="`my-calendar.task-table-detail.description-input.${task.task_id}`"
            class="inline-input"
            type="textarea"
            :rows="1"
            placeholder="Input description"
            @blur="() => saveInlineEdit(task, 'description')"
            @keydown="e => handleEditorKeydown(e, task, 'description')"
          />
        </div>
        <span
          v-else
          :data-testid="`my-calendar.task-table-detail.description-text.${task.task_id}`"
          class="description-font"
          style="display: block; min-height: 16px;"
        >{{ task.description || '\u00A0' }}</span>
      </div>
      <div class="flex items-center gap-3 cell-height">
        <img src="@/assets/my-calendar/task/gray-date.svg"/>
        <span :data-testid="`my-calendar.task-table-detail.due-date-text.${task.task_id}`" class="date-font">{{ formatDate(task.due_date) }}</span>
      </div>
      <div v-if="task.is_checked==='uncheck'" :data-testid="`my-calendar.task-table-detail.edit-btn.${task.task_id}`" class="edit-col flex items-center gap-1 cell-height" @click="editTask(task)">
        <img src="@/assets/my-calendar/task/edit.svg"/>
        <span class="edit-font">Edit</span>
      </div>
      <div v-if="task.is_checked==='uncheck'" :data-testid="`my-calendar.task-table-detail.delete-btn.${task.task_id}`" class="edit-col flex items-center gap-1 cell-height" @click="openDeleteTaskDialog(task)">
        <img src="@/assets/my-calendar/task/red-delete.svg"/>
        <span class="edit-font">Delete</span>
      </div>
    </div>
    <div v-if="convertedTaskData.length === 0" data-testid="my-calendar.task-table-detail.empty-state" class="no-task">
      <img src="@/assets/my-calendar/meeting-request/empty.png" alt="empty-meeting-request" />
      <div>
        <div>You have no tasks.</div>
        <div class="note-text">Start to add a new customized task.</div>
      </div>
    </div>
  </div>

  <DeleteTaskDialog
    v-if="deleteTaskDialogVisible"
    :visible="deleteTaskDialogVisible"
    @close="deleteTaskDialogVisible = false"
    @delete="deleteTask"
  />
</template>

<script setup>

import { ref, computed, reactive, nextTick } from 'vue'
import Checkbox from '@/NewLibrary/Checkbox.vue'
import Input from '@/NewLibrary/Input.vue'
import ArrowSmall from '@/NewLibrary/Loading/ArrowSmall.vue'
import { useStore } from 'vuex'
import moment from 'moment-timezone';
import DeleteTaskDialog from './DeleteTaskDialog.vue';

const store = useStore()
const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])

const { taskData, activeTaskTab } = defineProps(['taskData', 'activeTaskTab'])
const emit = defineEmits(['open-create-task'])
const convertedTaskData = computed(() => taskData.map(task => ({
  ...task,
  is_checked: task.is_completed ? 'checked' : 'uncheck'
})))

// Check if a task is overdue
const isTaskOverdue = (task) => {
  if (!task.due_date || task.is_completed) return false
  const dueDate = moment.utc(task.due_date).tz(calendarTimezone.value).startOf('day')
  const now = moment().tz(calendarTimezone.value)
  return now.isAfter(dueDate)
}

const editState = reactive({
  editingTaskId: null,
  editingField: null,
})

const editDrafts = reactive({})
const editorRefs = reactive({})
const saveLocks = reactive({})
const skipNextBlur = reactive({})
const loadingTaskIds = reactive({})

const cellKey = (taskId, field) => `${taskId}::${field}`

const isRowLoading = (taskId) => Boolean(loadingTaskIds[taskId])

const ensureDrafts = (taskId) => {
  if (!editDrafts[taskId]) editDrafts[taskId] = { title: '', description: '' }
}

const isEditing = (task, field) => {
  return editState.editingTaskId === task.task_id && editState.editingField === field
}

const setEditorRef = (taskId, field, el) => {
  if (!editorRefs[taskId]) editorRefs[taskId] = {}
  editorRefs[taskId][field] = el
}

const focusEditor = async (taskId, field) => {
  await nextTick()
  const inputVm = editorRefs?.[taskId]?.[field]
  const inputEl = inputVm?.$el?.querySelector?.('input, textarea')
  if (inputEl?.focus) inputEl.focus()
}

const startInlineEdit = async (task, field) => {
  if (task.is_checked !== 'uncheck') return
  if (isRowLoading(task.task_id)) return
  ensureDrafts(task.task_id)
  editDrafts[task.task_id].title = task.title ?? ''
  editDrafts[task.task_id].description = task.description ?? ''
  editState.editingTaskId = task.task_id
  editState.editingField = field
  await focusEditor(task.task_id, field)
}

const cancelInlineEdit = () => {
  editState.editingTaskId = null
  editState.editingField = null
}

const saveInlineEdit = async (task, field) => {
  if (!isEditing(task, field)) return

  const key = cellKey(task.task_id, field)
  if (skipNextBlur[key]) {
    skipNextBlur[key] = false
    return
  }
  if (saveLocks[key]) return
  saveLocks[key] = true

  const nextTitle = editDrafts[task.task_id]?.title ?? ''
  const nextDescription = editDrafts[task.task_id]?.description ?? ''
  const prevTitle = task.title ?? ''
  const prevDescription = task.description ?? ''
  const isUnchanged = nextTitle === prevTitle && nextDescription === prevDescription
  if (isUnchanged) {
    saveLocks[key] = false
    cancelInlineEdit()
    return
  }

  loadingTaskIds[task.task_id] = true
  const updateInput = {
    task_id: task.task_id,
    title: nextTitle,
    description: nextDescription,
  }
  try {
    await store.dispatch('calendar/updateTask', updateInput)
    await store.dispatch('calendar/fetchTasks')
    cancelInlineEdit()
  } finally {
    loadingTaskIds[task.task_id] = false
    saveLocks[key] = false
  }
}

const handleEditorKeydown = (e, task, field) => {
  if (e.key === 'Escape') {
    e.preventDefault()
    cancelInlineEdit()
    return
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    skipNextBlur[cellKey(task.task_id, field)] = true
    saveInlineEdit(task, field)
  }
}

async function toggleCheckbox(task, val) {
  loadingTaskIds[task.task_id] = true
  const isRecurringTask = Boolean(task?.rrule)
  const rawOccurrenceDate = task?.start_date || task?.due_date
  const occurrenceDate = rawOccurrenceDate
    ? moment.utc(rawOccurrenceDate).toISOString()
    : null
  try {
    const payload = isRecurringTask
      ? { id: task.task_id, occurrenceDate }
      : { id: task.task_id }
    if (val === 'checked') {
      await store.dispatch('calendar/completeTask', payload)
    } else {
      await store.dispatch('calendar/unCompleteTask', payload)
    }
    await store.dispatch('calendar/fetchTasks')
  } finally {
    loadingTaskIds[task.task_id] = false
  }
}


function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = moment.utc(dateString).tz(calendarTimezone.value);
  if (!date.isValid()) return 'N/A';

  if (date.hour() === 23 && date.minute() === 59 && date.second() === 59) {
    return date.format('MMM DD, YYYY')
  }

  return date.format('MMM DD, YYYY hh:mma');
}

function formattedPatientName(calendarId) {
  if (!calendarId) return 'N/A';
  const patient = store.state.calendar.rawPatientList.find(patient => patient.calendar_id === calendarId)
  if (!patient || !patient.first_name || !patient.last_name) return 'N/A';
  return patient.first_name + ' ' + patient.last_name
}

function editTask(task) {
  store.dispatch('calendar/setCurrentTaskInfo', { ...task, mode: 'edit' })
  emit('open-create-task')
}

const deleteTaskDialogVisible = ref(false)
const deleteTaskInfo = ref(null)
function openDeleteTaskDialog(task) {
  deleteTaskInfo.value = task
  deleteTaskDialogVisible.value = true
}
async function deleteTask() {
  deleteTaskDialogVisible.value = false
  store.dispatch('calendar/deleteTask', deleteTaskInfo.value.task_id)
  store.dispatch('calendar/setTaskLoading', true)
  await store.dispatch('calendar/fetchTasks')
  store.dispatch('calendar/setTaskLoading', false)
}

</script>

<style scoped>
.task-table-header {
  align-items: center;
  padding: 5px 12px;
  grid-gap: 20px;
  background: #F9FBFD;
  border-radius: 4px;
}
.header-text {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
  text-transform: uppercase;
}
.task-table-line{
  align-items: center;
  padding: 8px 12px;
  grid-gap: 20px; 
  transition: background 0.2s;
  border-radius: 4px;
  position: relative;
}
.task-table-line:hover{
  background: #F9FBFD;
}

.task-table-line.row-loading {
  pointer-events: none;
}

.row-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.row-loading-icon {
  width: 28px;
  height: 28px;
  transform-origin: center;
  animation: rotate 1.6s linear infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}
.task-table-line.checked,
.task-table-line.checked .date-font,
.task-table-line.checked .description-font,
.task-table-line.checked .name-font {
  background: #F7F7F7;
  color:#999999;
}

/* Overdue task styling - matches Figma design */
/* .task-table-line.overdue {
  background: );
} */
.task-table-line.overdue:hover {
  background: rgba(250, 234, 234, 0.50);
}
.task-table-line.overdue .title-font,
.task-table-line.overdue .description-font {
  color: #D10000;
}

.grid-cols-patient {
  display: grid;
  grid-template-columns: 2% 21% 15% 27% 15% 4% 7%; /* checkbox, title, patient, desc, date, edit, delete */
}
.grid-cols-task {
  display: grid;
  grid-template-columns: 2% 27% 37% 16% 4% 7%; /* checkbox, title, desc, date, edit, delete */
}
.edit-col {
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
}
.task-table-line:hover .edit-col {
  opacity: 1;
  pointer-events: auto;
}

.title-font,
.name-font,
.description-font,
.date-font {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.4px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name-font {
  font-size: 12px;
  color: #004879;
  cursor: pointer;
}
.date-font,
.description-font {
  color: #666666;
}
.edit-font {
  font-family: 'Inter', sans-serif;
  line-height: 1;
  letter-spacing: 0.4px;
  color: #666666;
  font-size: 14px;
}
.text-truncate{
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inline-editable {
  cursor: text;
 }

.cell-height {
  min-height: 42px;
  display: flex;
  align-items: center;
 }

.inline-editor {
  display: flex;
  align-items: center;
  width: 100%;
}

.inline-editor :deep(.el-textarea__inner),
.inline-editor :deep(.el-input__inner) {
  height: 16px;
  line-height: 16px;
}
.no-task{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 42px;
  font-family: Inter;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  text-align: center;
}
.note-text{
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #979797;
  margin-top: 8px;
}
 
.inline-input {
  width: 100%;
 }
 
.inline-input :deep(.el-input__wrapper){
  border: 1px solid #004879;
  box-shadow: none !important;
  background: #FFFFFF;
  padding: 8px;
  min-height: 34px;
  border-radius: 3px;
  margin-bottom: 4px;
  height: 32px;
 }

 .inline-input :deep(.new-library-input-basic .el-input__wrapper.is-focus),
 .inline-input :deep(.new-library-input-basic .el-input__wrapper:focus-within) {
  border-color: #004879 !important;
 }
 
.inline-input :deep(.el-input__inner){
  font-size: 14px;
  font-family: Inter;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #333333;
  padding: 0;
 }
 
.inline-input :deep(.el-input__inner::placeholder){
  color: #666666;
 }
</style>